import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DeviceActionProps,
  DeviceActionStepsProps,
} from '@components/Actions/ActionsModal/types';
import ActionTypes from '@utils/deviceActions';
import rolesConstants from '@utils/rolesConstants';
import { Form, FormInstance, Radio } from 'antd';
import { IDevice } from '@pages/Assets/Inventory/types';
import useCancellationAndSubstitution from './useCancellationAndSubstitution';
import useCancellationWithdrawal from './useCancellationWithdrawal';

interface BrokerCancellationProps {
  devices: IDevice[];
  form: FormInstance<any>;
  isUpload?: boolean;
}

const useBrokerCancellation = ({
  devices,
  form,
  isUpload,
}: BrokerCancellationProps): DeviceActionProps => {
  const { t } = useTranslation([
    'actions-modal',
    'device-actions',
    'attributes',
  ]);
  const actionType = Form.useWatch('actionType', form);

  const devicesPayload = useMemo(
    (): { msisdn: string }[] =>
      devices?.map(device => ({
        msisdn: device.msisdn,
      })),
    [devices],
  );

  const cancellationAndSubstitutionAction = useCancellationAndSubstitution(
    form,
    devicesPayload,
  );

  const cancellationWithdrawalAction =
    useCancellationWithdrawal(devicesPayload);

  const selectedAction: DeviceActionProps = useMemo(() => {
    const actions: { [key: number]: DeviceActionProps } = {
      [ActionTypes.BROKER_CANCELLATION]: cancellationAndSubstitutionAction,
      [ActionTypes.BROKER_CANCELLATION_WAIVER]: cancellationWithdrawalAction,
    };

    return actions[actionType];
  }, [
    cancellationAndSubstitutionAction,
    actionType,
    cancellationWithdrawalAction,
  ]);

  const steps: DeviceActionStepsProps[] = useMemo(() => {
    const defaultSteps: DeviceActionStepsProps[] = [
      {
        title: t('broker-cancellation.action'),
        name: 'actionType',
        showWhenUpload: true,
        content: (
          <Radio.Group optionType="button" disabled={isUpload && !devices}>
            <Radio value={ActionTypes.BROKER_CANCELLATION}>
              {t('broker-cancellation.cancel')}
            </Radio>
            <Radio value={ActionTypes.BROKER_CANCELLATION_WAIVER}>
              {t('broker-cancellation.withdraw')}
            </Radio>
          </Radio.Group>
        ),
      },
    ];

    if (selectedAction?.steps) {
      defaultSteps.push(...selectedAction?.steps);
    }

    return defaultSteps;
  }, [devices, isUpload, selectedAction?.steps, t]);

  const payloadToSend = useMemo(
    () => ({
      ...selectedAction?.payloadToSend,
      action:
        selectedAction?.payloadToSend?.action ??
        ActionTypes.BROKER_CANCELLATION,
      devices: devices?.map(device => ({
        msisdn: device.msisdn,
      })),
    }),
    [devices, selectedAction?.payloadToSend],
  );

  return {
    ...selectedAction,
    payloadToSend,
    title: t(`device-actions:labels.mot.${rolesConstants.BROKER_CANCELLATION}`),
    steps,
  };
};

export default useBrokerCancellation;
